import React, { useRef } from 'react';
import { Flex, InputNumber, Switch } from 'antd';
import { useIntl } from 'umi';
import { F } from '@/utils/intl';
import { OverbookingModel } from '@/typings/models/Location';
import { ServiceTypeModel } from '@/typings/models/ServiceType';
import ToggleCard from '@/components/ToggleCard/ToggleCard';
import styles from './index.less';

interface ServiceOverbookingSettingsProps {
  value?: OverbookingModel;
  serviceType?: ServiceTypeModel;
  onChange: (value: OverbookingModel) => void;
}

/**
 * The service type's own overbooking. A capacity wins over the unlimited flag,
 * matching how the backend resolves the pair.
 */
export const serviceTypeOverbooking = (
  serviceType?: ServiceTypeModel,
): OverbookingModel => {
  const capacity = serviceType?.additional_capacity ?? 0;
  if (capacity > 0) return { mode: 'limited', limit: capacity };
  return serviceType?.unlimited_booking
    ? { mode: 'unlimited', limit: null }
    : { mode: 'off', limit: null };
};

/**
 * What is actually in force for this branch service: its own setting, or the
 * service type's while it still inherits.
 */
export const effectiveOverbooking = (
  value?: OverbookingModel,
  serviceType?: ServiceTypeModel,
): OverbookingModel =>
  !value || value.mode === 'inherit'
    ? serviceTypeOverbooking(serviceType)
    : value;

const ServiceOverbookingSettings: React.FC<ServiceOverbookingSettingsProps> = ({
  value,
  serviceType,
  onChange,
}) => {
  const intl = useIntl();

  const effective = effectiveOverbooking(value, serviceType);
  const isOn = effective.mode === 'unlimited' || effective.mode === 'limited';
  const isLimited = effective.mode === 'limited';

  // Turning overbooking off drops the limit from the value, so the last one is
  // kept here to hand back when the limit is switched on again.
  const lastLimit = useRef<number | null>(effective.limit);
  if (effective.limit != null) lastLimit.current = effective.limit;

  // Every interaction writes an explicit override: a branch service that was
  // still inheriting stops following its service type as soon as this is touched.
  const handleToggle = (checked: boolean) => {
    onChange(
      checked ? { mode: 'unlimited', limit: null } : { mode: 'off', limit: null },
    );
  };

  const handleLimitToggle = (checked: boolean) => {
    onChange(
      checked
        ? { mode: 'limited', limit: lastLimit.current ?? 1 }
        : { mode: 'unlimited', limit: null },
    );
  };

  const handleLimitChange = (limit: number | null) => {
    // An emptied input is not a limit yet — hold the last good value.
    if (limit == null) return;
    onChange({ mode: 'limited', limit });
  };

  return (
    <ToggleCard
      title={<F id="pages.serviceTypes.overbooking" defaultMessage="Overbooking" />}
      description={
        <F
          id="pages.serviceTypes.overbooking.description"
          defaultMessage="Allow more appointments to be booked than the available capacity at this location."
        />
      }
      checked={isOn}
      onChange={handleToggle}
    >
      {isOn && (
        <div className={styles.overbookingLimitRow}>
          <Flex align="center" gap={12}>
            <Switch size="small" checked={isLimited} onChange={handleLimitToggle} />
            <span
              title={intl.formatMessage({
                id: 'pages.serviceTypes.overbookingLimit.tooltip',
                defaultMessage: 'Without setting a limit, overbooking is unlimited.',
              })}
            >
              <F
                id="pages.serviceTypes.overbookingLimit"
                defaultMessage="Overbooking Limit"
              />
            </span>
            {isLimited && (
              <InputNumber
                variant="underlined"
                min={1}
                autoFocus
                value={effective.limit ?? 1}
                onChange={handleLimitChange}
              />
            )}
          </Flex>
        </div>
      )}
    </ToggleCard>
  );
};

export default ServiceOverbookingSettings;
